import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SecurityIcon from '@mui/icons-material/Security';

const PerfilDrawer = ({ open, onClose, user, userRoles = [], onFotoAtualizada }) => {
  const [editandoFoto, setEditandoFoto] = useState(false);
  const [fotoUrl, setFotoUrl] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  useEffect(() => {
    if (user?.foto_perfil) {
      setFotoUrl(user.foto_perfil);
    }
  }, [user]);

  const getDiasExpiracaoSenha = () => {
    if (!user?.dias_expiracao_senha && user?.dias_expiracao_senha !== 0) return null;
    return user.dias_expiracao_senha;
  };

  const getUserInitials = () => {
    if (user?.nome) {
      const names = user.nome.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return user.nome.substring(0, 2).toUpperCase();
    }
    return 'OP';
  };

  const validarUrl = (url) => {
    if (!url) return true;
    if (url.length > 500) return 'URL deve ter no máximo 500 caracteres';
    try {
      new URL(url);
      return true;
    } catch {
      return 'URL inválida';
    }
  };

  const handleSalvarFoto = async () => {
    const validacao = validarUrl(fotoUrl);
    if (validacao !== true) {
      setSnackbar({ open: true, severity: 'error', message: validacao });
      return;
    }

    setSalvando(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ foto_perfil: fotoUrl || null })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.mensagem || 'Erro ao atualizar foto');
      }

      const novoUser = { ...user, foto_perfil: fotoUrl || null };
      localStorage.setItem('user', JSON.stringify(novoUser));
      
      if (onFotoAtualizada) {
        onFotoAtualizada(fotoUrl);
      }

      setEditandoFoto(false);
      setSnackbar({ open: true, severity: 'success', message: 'Foto atualizada com sucesso!' });
    } catch (error) {
      setSnackbar({ open: true, severity: 'error', message: error.message });
    } finally {
      setSalvando(false);
    }
  };

  const handleCancelarEdicao = () => {
    setFotoUrl(user?.foto_perfil || '');
    setEditandoFoto(false);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 380 },
            bgcolor: 'background.paper'
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={700}>
              Meu Perfil
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={user?.foto_perfil || ''}
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: 'primary.main',
                  fontSize: '2.5rem',
                  border: '4px solid',
                  borderColor: 'primary.main'
                }}
              >
                {!user?.foto_perfil && getUserInitials()}
              </Avatar>
              <IconButton
                size="small"
                onClick={() => setEditandoFoto(true)}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {editandoFoto ? (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="URL da Foto de Perfil"
                value={fotoUrl}
                onChange={(e) => setFotoUrl(e.target.value)}
                placeholder="https://exemplo.com/foto.jpg"
                size="small"
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelarEdicao}
                  disabled={salvando}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSalvarFoto}
                  disabled={salvando}
                >
                  {salvando ? <CircularProgress size={20} /> : 'Salvar'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', mb: 3, cursor: 'pointer' }}
              onClick={() => setEditandoFoto(true)}
            >
              Clique no ícone de edição para alterar a foto
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonIcon color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Nome</Typography>
                <Typography variant="body1" fontWeight={500}>{user?.nome || '-'}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AccountCircleIcon color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Usuário</Typography>
                <Typography variant="body1" fontWeight={500}>{user?.nome_de_usuario || '-'}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SecurityIcon color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={user?.ativo !== false ? 'Ativo' : 'Bloqueado'}
                    color={user?.ativo !== false ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>

            {getDiasExpiracaoSenha() !== null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SecurityIcon color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Expiração de Senha</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {getDiasExpiracaoSenha()} dia(s)
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Minhas Roles
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {userRoles.length > 0 ? (
              userRoles.map((role, index) => (
                <Chip key={index} label={role} variant="outlined" size="small" />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhuma role atribuída
              </Typography>
            )}
          </Box>
        </Box>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PerfilDrawer;
